import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { X } from "lucide-react";
import { FitCalculator } from "@/components/site/FitCalculator";
import "./CostCompare.css";

const alternatives = [
  {
    price: "~$1.2k",
    unit: "/ month",
    name: "Virtual assistant",
    kill: "Admin covered. You still manage outreach and need a closer.",
  },
  {
    price: "~$8k",
    unit: "/ month",
    name: "One US appointment setter",
    kill: "Meetings booked. You still need a manager, a closer and sales tools.",
  },
  {
    price: "~$400k",
    unit: "/ year",
    name: "Build in-house",
    kill: "You recruit, train and manage every role.",
  },
];

export const CostCompare = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="maths" className="bg-surface-dark border-t border-ink" data-testid="cost-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-14">
        <h2 className="font-display font-semibold tracking-[-0.02em] text-4xl md:text-5xl lg:text-6xl leading-[0.95] text-ink max-w-4xl text-balance">
          Selling in <span className="font-editorial italic font-medium text-cobalt">two weeks.</span>
        </h2>

        <div ref={ref} className="mt-8 relative grid grid-cols-1 md:grid-cols-2 border border-ink">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="cost-timing-card relative bg-cobalt text-white p-8 md:p-12 overflow-hidden"
          >
            <p className="relative font-mono text-base font-semibold uppercase tracking-[0.14em] text-white mb-6 text-pretty">
              With GTM
            </p>
            <span className="cost-timing-value relative block font-display font-semibold tracking-tighter leading-[0.95] text-white">
              2 weeks
            </span>
            <p className="relative mt-6 text-lg md:text-xl font-light text-white leading-snug max-w-sm text-pretty">
              Buyers identified. Campaigns live. Your team selling.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.12 }}
            className="cost-timing-card relative bg-paper p-8 md:p-12 border-t md:border-t-0 md:border-l border-ink overflow-hidden"
          >
            <p className="relative font-mono text-base font-semibold uppercase tracking-[0.14em] text-ink mb-6 text-pretty">
              In-house
            </p>
            <span className="cost-timing-value relative block font-display font-semibold tracking-tighter leading-[0.95] text-ink">
              6 months
            </span>
            <p className="relative mt-6 text-lg md:text-xl font-light text-ink leading-snug max-w-sm text-pretty">
              An estimated $400k a year for a manager, setters, a closer and tools. You handle hiring and training.
            </p>
          </motion.div>

          <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 h-16 w-16 items-center justify-center bg-flame text-white font-display font-semibold text-xl border border-ink shadow-hard">
            <motion.span
              animate={{ rotate: [-8, 8, -8] }}
              transition={{ duration: 4.5, ease: "easeInOut", repeat: Infinity }}
              className="block"
            >
              VS
            </motion.span>
          </div>
        </div>

        <div className="mt-8">
          <h3 className="font-display font-semibold text-2xl md:text-4xl tracking-tight text-ink mb-5 text-balance">
            What you still have to manage.
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-ink border border-ink">
            {alternatives.map((a, i) => (
              <motion.div
                key={a.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="p-7 md:p-8"
                style={{ backgroundColor: "#F3E4E0" }}
                data-testid={`cost-alt-${i}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-end gap-1.5">
                    <span className="font-display font-semibold text-4xl md:text-5xl tracking-tighter leading-[0.85] text-[#991B1B]">
                      {a.price}
                    </span>
                    <span className="text-base text-[#991B1B] pb-1">{a.unit}</span>
                  </div>
                  <span
                    className="flex h-8 w-8 items-center justify-center border-2 border-[#DC2626] text-[#DC2626]"
                    aria-hidden
                  >
                    <X size={18} strokeWidth={2.75} />
                  </span>
                </div>
                <p className="mt-4 font-display font-semibold text-xl tracking-tight text-[#991B1B] text-pretty">{a.name}</p>
                <p className="mt-2 text-lg font-light text-ink leading-snug text-pretty">{a.kill}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <FitCalculator />
      </div>
    </section>
  );
};
